// listenerMiddleware.js
import { createListenerMiddleware, isAnyOf } from "@reduxjs/toolkit";
import { isEmpty, omitBy, throttle, debounce } from "lodash";
import axios from 'lib/axios'
import { callPost } from "../api/base";

import { heartbeatActions as ownerActions } from "./common/owners";
import { heartbeatActions as threadActions } from "./common/threads";
import { heartbeatActions as messageActions } from "./common/messages";
import { heartbeatActions as userActions } from "./common/users";

import importData from './utils/import_data'

const SLICES = ['owners', 'message_threads', 'messages', 'users']
const ACTIONS = {
  owners: ownerActions,
  message_threads: threadActions,
  messages: messageActions,
  users: userActions
}

const INTERVAL = 5000
const REF_TIMEOUT = 25000

function gatherRecentlyAdded(state) {
  const updates = {}
  for (let slice of SLICES) {
    const itemState = state[slice].state
    const refs = state[slice].refs

    updates[slice] = Object.keys(state[slice].recentUpdates).filter(id => refs[id] > 0 && !itemState[id])
  }

  return omitBy(updates, isEmpty)
}

function gatherNeededRemovals(state) {
  const removals = {}
  const updatedBefore = (new Date()).getTime() - REF_TIMEOUT
  for (let slice of SLICES) {
    const refs = state[slice].refs
    const itemState = state[slice].state
    const refUpdates = state[slice].refUpdates

    removals[slice] = Object.keys(state[slice].recentRemovals).filter((id) => {
      return refs[id] <= 0 && itemState[id] && refUpdates[id] < updatedBefore
    })
  }

  return omitBy(removals, isEmpty)
}

function addReferenceItems({ dispatch, getState }) {
  const state = getState()
  const additions = gatherRecentlyAdded(state)
  
  // Set the items I am going to load as loading
  for (let slice in additions) {
    dispatch(ACTIONS[slice].load(additions[slice]))
  }

  if (isEmpty(additions))
    return

  callPost('/api/messaging/lookup', additions).then(({data}) => importData(dispatch, data) )
}

function removeItems({ dispatch, getState }) {
  const state = getState()
  const removals = gatherNeededRemovals(state)

  //console.log("Removals", removals)
  for (let slice in ACTIONS) {
    if (isEmpty(removals[slice]))
      continue

    dispatch(ACTIONS[slice].clearRemovals(removals[slice]))
    for (let slice in removals) 
      dispatch(ACTIONS[slice].remove(removals[slice]))
  }
}

// background heartbeat task

// Create throttled and debounce fetch
const throttledFetch = throttle(addReferenceItems, 1000);
const debouncedThrottledFetch = debounce(throttledFetch, 50);

export function registerListeners(listenerMiddleware, initAction) {
  listenerMiddleware.startListening({
    actionCreator: initAction,
    effect: async (action, listenerApi) => {
      // run a "heartbeat" loop in the background
      while (true) {
        await listenerApi.delay(INTERVAL);

        removeItems(listenerApi)
      }
    },
  });

  for (let slice in ACTIONS) {
    listenerMiddleware.startListening({
      actionCreator: ACTIONS[slice].triggerUpdate,
      effect: async (action, listenerApi) => {
        debouncedThrottledFetch(listenerApi);
      },
    });
  }
}